import grapesjs from "grapesjs";
import gjsPresetNewsletter from "grapesjs-preset-newsletter";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { useIntl } from "react-intl";
import "grapesjs/dist/css/grapes.min.css";
import { DEFAULT_EMAIL_HTML, isEmptyEmailHtml } from "./emailHtmlUtils";

export interface EmailVisualBuilderHandle {
  setContent: (html: string) => void;
  getContent: () => string;
}

interface EmailVisualBuilderProps {
  html: string;
  onHtmlChange: (html: string) => void;
}

const NEWSLETTER_PLUGIN = "gjs-preset-newsletter";
const BLOCKS_ID = "email-mkt-gjs-blocks";
const STYLES_ID = "email-mkt-gjs-styles";
const LAYERS_ID = "email-mkt-gjs-layers";

const getInlinedHtml = (editor: any) =>
  new Promise<string>((resolve) => {
    editor.runCommand("gjs-get-inlined-html", {
      callback: (inlinedHtml: string) => resolve(inlinedHtml || ""),
    });
  });

const polishEditorChrome = (editor: any) => {
  editor.Panels.removePanel("views");
};

export const EmailVisualBuilder = forwardRef<
  EmailVisualBuilderHandle,
  EmailVisualBuilderProps
>(({ html, onHtmlChange }, ref) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<any>(null);
  const syncingRef = useRef(false);
  const onHtmlChangeRef = useRef(onHtmlChange);

  useEffect(() => {
    onHtmlChangeRef.current = onHtmlChange;
  }, [onHtmlChange]);

  useImperativeHandle(ref, () => ({
    setContent: (nextHtml: string) => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }
      syncingRef.current = true;
      editor.setComponents(nextHtml || DEFAULT_EMAIL_HTML);
      getInlinedHtml(editor).then((inlined) => {
        onHtmlChangeRef.current(inlined);
        syncingRef.current = false;
      });
    },
    getContent: () => {
      const editor = editorRef.current;
      if (!editor) {
        return html;
      }
      return editor.getHtml();
    },
  }));

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const editor = grapesjs.init({
      container: containerRef.current,
      height: "100%",
      width: "auto",
      fromElement: false,
      storageManager: false,
      noticeOnUnload: false,
      assetManager: {
        embedAsBase64: true,
      },
      blockManager: {
        appendTo: `#${BLOCKS_ID}`,
      },
      styleManager: {
        appendTo: `#${STYLES_ID}`,
      },
      layerManager: {
        appendTo: `#${LAYERS_ID}`,
      },
      selectorManager: {
        componentFirst: true,
      },
      deviceManager: {
        devices: [
          { name: "Desktop", width: "" },
          { name: "Tablet", width: "768px", widthMedia: "992px" },
          { name: "Mobile", width: "320px", widthMedia: "480px" },
        ],
      },
      canvas: {
        styles: [
          "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
        ],
      },
      plugins: [gjsPresetNewsletter],
      pluginsOpts: {
        [NEWSLETTER_PLUGIN]: {
          modalTitleImport: intl.formatMessage({
            id: "app.emailMarketing.editor.importTemplate",
          }),
          showBlocksOnLoad: true,
          useCustomTheme: false,
        },
      },
    }) as any;

    editorRef.current = editor;

    const initialHtml =
      html && !isEmptyEmailHtml(html) ? html : DEFAULT_EMAIL_HTML;
    editor.setComponents(initialHtml);
    getInlinedHtml(editor).then((inlined) => {
      if (inlined) {
        onHtmlChangeRef.current(inlined);
      }
    });

    const handleUpdate = () => {
      if (syncingRef.current) {
        return;
      }
      getInlinedHtml(editor).then((inlined) => {
        onHtmlChangeRef.current(inlined);
      });
    };

    editor.on("update", handleUpdate);
    editor.on("component:update", handleUpdate);
    editor.on("load", () => polishEditorChrome(editor));

    return () => {
      editor.destroy();
      editorRef.current = null;
    };
  }, [intl]);

  const blocksLabel = intl.formatMessage({
    id: "app.emailMarketing.editor.blocksPanel",
  });
  const stylesLabel = intl.formatMessage({
    id: "app.emailMarketing.editor.stylesPanel",
  });
  const layersLabel = intl.formatMessage({
    id: "app.emailMarketing.editor.layersPanel",
  });

  return (
    <div className="email-mkt__gjs-studio">
      <aside className="email-mkt__gjs-panel email-mkt__gjs-panel--blocks">
        <header className="email-mkt__gjs-panel-head">{blocksLabel}</header>
        <div id={BLOCKS_ID} className="email-mkt__gjs-panel-body" />
      </aside>

      <div className="email-mkt__gjs-stage">
        <div className="email-mkt__gjs-root" ref={containerRef} />
      </div>

      <aside className="email-mkt__gjs-panel email-mkt__gjs-panel--styles">
        <header className="email-mkt__gjs-panel-head">{stylesLabel}</header>
        <div className="email-mkt__gjs-layers-wrap">
          <span className="email-mkt__gjs-layers-label">{layersLabel}</span>
          <div id={LAYERS_ID} className="email-mkt__gjs-layers" />
        </div>
        <div id={STYLES_ID} className="email-mkt__gjs-panel-body" />
      </aside>
    </div>
  );
});

EmailVisualBuilder.displayName = "EmailVisualBuilder";
